import {Box, HBox} from '@buncha/components/core/view';
import {ClientType, getClientType} from '@buncha/common/types/common';
import {CustomText, H6Text, PSmallText} from '@buncha/components/core/text';
import {Dimensions, Linking, StyleSheet} from 'react-native';
import {
  OrderWithAddress,
  driverStatuses,
} from '@buncha/driver/types/driverOrders';
import React, {useCallback, useEffect, useMemo} from 'react';
import {getMapUrl, getWidth, useCheckIfB2BStore} from '@buncha/utils/common';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import Call from '@buncha/components/core/icon/Call';
import {DeliveryLocationStatus} from '@buncha/batchDetails/types';
import DeliveryNote from '../../common/nameAndAddressDetails/deliveryNote';
import {Divider} from 'native-base';
import {DriverScreens} from '@buncha/driver/navigation/type';
import {EventType} from '@buncha/config/analytics/type';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import LocationIcon from '@buncha/components/core/icon/LocationIcon';
import {MainScreens} from '@buncha/navigation/types';
import {MapChoice} from '@buncha/onBoarding/types';
import {Modules} from '@buncha/utils/types';
import {PButton} from '@buncha/components/core/Button';
import PersonRoundIcon from '@buncha/components/core/icon/PersonRoundIcon';
import {Task} from '@buncha/config/onfleet/types';
import {Touchable} from '@buncha/components/core/touchable';
import {gqlService} from '@buncha/config/graphQL';
import {navigation} from '@buncha/config/navigation';
import {scaleFonts} from '@buncha/appStyles/style/font';
import {useAppSelector} from '@buncha/config/redux/hooks';
import {useFetchMaskedNumber} from '@buncha/itemDetails/hooks/ItemDetails';
import {useGetLocale} from '@buncha/localization/hooks';
import {useIsMeijerStore} from '@buncha/home/hooks/home';

interface Props {
  task: Task;
  index: number;
  order: OrderWithAddress;
}

const ActiveDriverOrderCell = (props: Props) => {
  const {task, index, order} = props;
  const theme = useColorThemeType('main');
  const muli = useFontTheme('muli');
  const deliveryNote = order?.deliveryLocation?.deliveryNote;
  const isNotStarted =
    task.metadata?.orderId &&
    task?.id &&
    !driverStatuses.has(order?.deliveryLocation?.deliveryStatus);
  const user = useAppSelector(gstate => gstate.onboarding.currentUser);
  const mapChoice = user?.mapChoice || MapChoice.googleMaps;
  const driverTasks = useAppSelector(gState => gState.home.driverTasks);
  const {checkIfB2BStore} = useCheckIfB2BStore();
  const {body, button} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};
  const {fetchMaskedNumber, info} = useFetchMaskedNumber();

  const address = order?.deliveryLocation?.userAddress?.address;
  const onfleetAddress = `${task?.destinationAddress?.address} ${task?.destinationAddress?.city} ${task?.destinationAddress?.postalCode}`;

  const isAddressCorrect = useMemo(() => {
    if (!address || !onfleetAddress) return false;

    return address.split(',').join('') === onfleetAddress;
  }, [address, onfleetAddress]);

  const isFirstItem = driverTasks?.[0]?.id === task?.id;
  const isB2BStore = useIsMeijerStore(order?.run?.store?.id);
  const canGoToLoadVanAt =
    order?.deliveryLocation?.deliveryStatus ===
      DeliveryLocationStatus.awaitingHandover && isFirstItem;

  const contactCustomer = useCallback(() => {
    Linking.openURL(
      `tel:${info?.data?.fetchMaskedNumber ?? task.recipientPhone}`,
    );
  }, [info?.data?.fetchMaskedNumber, task.recipientPhone]);

  const getStatusText = useMemo(() => {
    if (task.state) return button?.inTransit;
    if (!task.metadata.orderId) return button?.manualTask;
    return button?.orderPacked;
  }, [
    button?.inTransit,
    button?.manualTask,
    button?.orderPacked,
    task.metadata.orderId,
    task.state,
  ]);

  const getColors = useMemo(() => {
    if (task.state)
      return {
        background: theme?.background?.blue?.[100],
        foreground: theme?.background?.blue?.[100],
      };
    return {
      background: theme?.background?.gray?.[200],
      foreground: theme?.text?.gray?.[100],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.state]);

  const getButtonText = useMemo(() => {
    if (canGoToLoadVanAt) return button?.startRoute;
    if (task.state) return button?.continue;
    return button?.startTask;
  }, [
    button?.continue,
    button?.startRoute,
    button?.startTask,
    canGoToLoadVanAt,
    task.state,
  ]);

  const openUrl = useCallback(() => {
    const coords = {
      lat: task.destinationAddress.locationLatitude,
      lng: task.destinationAddress.locationLongitude,
    };
    Analytics.eventWithProps('Open map for driver', {
      mapChoice: mapChoice,
      buyerLat: task.destinationAddress.locationLatitude,
      buyerLon: task.destinationAddress.locationLongitude,
      type: EventType.cta,
      userType: 'driver',
    });
    Linking.openURL(getMapUrl(mapChoice, coords));
  }, [
    mapChoice,
    task.destinationAddress.locationLatitude,
    task.destinationAddress.locationLongitude,
  ]);

  const navigateToOrderDetails = useCallback(() => {
    gqlService?.updateIsMeijer(checkIfB2BStore(order?.run?.store?.id));
    if (canGoToLoadVanAt)
      return navigation.navigate(MainScreens.Driver, {
        screen: DriverScreens.LoadVanAt,
        params: {runId: order?.runId},
      });

    Analytics.eventWithProps(`${getButtonText} Delivery`, {
      type: EventType.cta,
      taskId: task.id,
      userType: 'driver',
      countOfBarcodes: task.barcodes?.length,
      countOfItems: task?.quantity,
      countOfBags: order?.bagsUsed,
      runId: task?.metadata?.runId,
      client: getClientType(isB2BStore),
      ...(getClientType(isB2BStore) === ClientType.meijer && {
        legacyId: order?.id,
      }),
      ...(getClientType(isB2BStore) === ClientType.buncha && {
        orderId: order?.id,
      }),
    });

    navigation.navigate(MainScreens.Driver, {
      screen: DriverScreens.OrderDetails,
      params: {taskId: task.id},
    });
  }, [
    checkIfB2BStore,
    order?.run?.store?.id,
    order?.runId,
    order?.bagsUsed,
    order?.id,
    canGoToLoadVanAt,
    task.id,
    task.barcodes?.length,
    task?.quantity,
    task?.metadata?.runId,
    getButtonText,
    isB2BStore,
  ]);

  useEffect(() => {
    fetchMaskedNumber({
      orderId: order?.id,
      runId: order?.runId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      style={[
        styles.parentContainerBox,
        {borderColor: theme?.background?.gray?.[100]},
      ]}>
      <HBox style={styles.parentBox}>
        <HBox>
          <Box
            style={[
              styles.countBox,
              {
                backgroundColor: theme?.text?.gray?.[200],
              },
            ]}>
            <H6Text
              style={{
                color: theme?.text?.gray?.[100],
              }}>
              {index}
            </H6Text>
          </Box>
          <HBox
            style={[
              styles.statusCell,
              styles.borderWidth,
              {borderColor: getColors?.background},
            ]}>
            <Box
              style={[
                styles.dot,
                {
                  backgroundColor: getColors?.foreground,
                },
              ]}
            />
            <H6Text
              style={{
                fontFamily: muli?.[700]?.normal,
                color: getColors?.foreground,
              }}>
              {getStatusText}
            </H6Text>
          </HBox>
        </HBox>
        <Box>
          <If condition={Math.round(task?.metadata?.orderId)}>
            <Box
              style={[
                styles.statusCell,
                {backgroundColor: theme?.background?.gray?.[200]},
              ]}>
              <H6Text style={{fontFamily: muli?.[700]?.normal}}>
                {body?.ordID}
                {Math.round(task?.metadata?.orderId)}
              </H6Text>
            </Box>
          </If>
          <If condition={Math.round(task?.metadata?.externalOrderId)}>
            <Box
              style={[
                styles.statusCell,
                {backgroundColor: theme?.background?.gray?.[200]},
              ]}>
              <H6Text
                style={[
                  styles.textAlignRight,
                  {fontFamily: muli?.[700]?.normal},
                ]}>
                #{Math.round(task?.metadata?.externalOrderId)}
              </H6Text>
            </Box>
          </If>
        </Box>
      </HBox>

      <HBox>
        <HBox style={styles.buyerBox}>
          <PersonRoundIcon fill={theme?.icon?.blue?.[300]} />
          <Box style={styles.nameBox}>
            <H6Text
              numberOfLines={1}
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.blue?.[300],
                  fontFamily: muli?.[700]?.normal,
                },
              ]}>
              {task.recipientName}
            </H6Text>
            <CustomText
              numberOfLines={1}
              style={[
                styles.textAlignment,
                {
                  color: theme?.text?.gray?.[100],
                  fontFamily: muli?.[600]?.normal,
                },
              ]}>
              {address || onfleetAddress}
            </CustomText>
            <If condition={!isAddressCorrect}>
              <PSmallText
                style={[
                  styles.textAlignment,
                  {
                    color: theme?.text?.red?.[100],
                    fontFamily: muli?.[600]?.normal,
                  },
                ]}>
                {body?.addressMismatch}
              </PSmallText>
            </If>
          </Box>
        </HBox>
        <If condition={info?.data?.fetchMaskedNumber ?? task.recipientPhone}>
          <Touchable onPress={contactCustomer}>
            <Call viewBox="0 -3 25 20" fill={theme?.icon?.green?.[100]} />
          </Touchable>
        </If>
      </HBox>

      <If condition={deliveryNote}>
        <DeliveryNote deliveryNote={deliveryNote} />
        <Divider />
      </If>
      <HBox style={styles.footerContainer}>
        <Touchable
          onPress={openUrl}
          style={[
            styles.locationTouchable,
            {borderColor: theme?.icon?.green?.[100]},
          ]}>
          <LocationIcon
            style={styles.icon}
            viewBox="-3 0 20 20"
            fill={theme?.icon?.green?.[100]}
          />
        </Touchable>
        <PButton
          onPress={navigateToOrderDetails}
          title={getButtonText}
          style={[
            styles.button,
            {
              backgroundColor: theme?.background?.green?.[100],
            },
          ]}
          disabled={(!isFirstItem || isNotStarted) && !canGoToLoadVanAt}
          titleStyle={styles.buttonTitle}
        />
      </HBox>
      <If condition={isNotStarted}>
        <CustomText
          style={[
            styles.disclaimerText,
            {
              color: theme?.text?.red?.[100],
              fontFamily: muli[600].normal,
            },
          ]}>
          {body?.pendingTask}
        </CustomText>
      </If>
    </Box>
  );
};

export default React.memo(ActiveDriverOrderCell);

const styles = StyleSheet.create({
  parentContainerBox: {
    borderWidth: 1,
    paddingHorizontal: 15,
    paddingVertical: 10,
    borderRadius: 20,
    width: getWidth(Dimensions.get('window').width * 0.88),
    alignSelf: 'center',
    marginVertical: 10,
  },
  parentBox: {
    // marginBottom: 10,
  },
  statusCell: {
    borderRadius: 20,
    paddingHorizontal: 7,
    paddingVertical: 2,
    marginBottom: 2,
  },
  externalStatusCell: {
    paddingVertical: 2,
    // marginHorizontal: -15,
    marginTop: 10,
  },
  borderWidth: {
    borderWidth: 1,
  },
  dot: {
    height: 8,
    width: 8,
    borderRadius: 50,
    marginRight: 5,
  },
  nameBox: {
    alignItems: 'flex-start',
    flex: 1,
    marginLeft: 10,
  },
  textAlignment: {
    textAlign: 'left',
    width: '100%',
  },
  buyerBox: {
    flex: 0.95,
    marginTop: 10,
    alignItems: 'center',
  },
  locationTouchable: {
    borderWidth: 1,
    width: '25%',
    marginRight: 10,
    borderRadius: 50,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 5,
    height: scaleFonts(50),
  },
  icon: {
    height: 30,
    width: 21,
  },
  button: {
    width: '72%',
    borderRadius: 50,
    height: scaleFonts(50),
  },
  buttonTitle: {
    fontSize: scaleFonts(16),
  },
  footerContainer: {
    justifyContent: 'flex-start',
    marginTop: 15,
  },
  countBox: {
    borderRadius: 100,
    paddingHorizontal: 7,
    marginRight: 5,
    paddingBottom: 1,
  },
  disclaimerText: {
    textAlign: 'left',
    marginTop: 5,
  },
  textAlignRight: {
    textAlign: 'right',
  },
});
